import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalBody,
  RadioButtonGroup,
  RadioButton,
  TextInput,
} from '@carbon/react';
import styles from './royalty-rate-modal.module.scss';
import { RoyaltyRateOption, ROYALTY_RATE_OPTIONS } from '../../constants';

interface RoyaltyRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  customRoyaltyRate: string;
  onApply: (rate: string) => void;
}

export const RoyaltyRateModal: React.FC<RoyaltyRateModalProps> = ({
  isOpen,
  onClose,
  customRoyaltyRate,
  onApply,
}) => {
  const [royaltyRate, setRoyaltyRate] = useState<string>(customRoyaltyRate);
  const [customRateError, setCustomRateError] = useState<string>('');

  const isCustomRate = !Object.values(ROYALTY_RATE_OPTIONS).includes(
    royaltyRate as RoyaltyRateOption
  );

  useEffect(() => {
    setRoyaltyRate(customRoyaltyRate);
    setCustomRateError('');
  }, [customRoyaltyRate, isOpen]);

  const isDirty = () => {
    return royaltyRate !== customRoyaltyRate && !customRateError;
  };

  const handleCustomRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.trim();
    setRoyaltyRate(rawValue);

    if (rawValue === '') {
      setCustomRateError('Please enter a value');
      return;
    }

    if (!/^[\d.%]+$/.test(rawValue)) {
      setCustomRateError(
        'Enter only numbers with "%" or a decimal value (e.g., 12% or 0.12).'
      );
      return;
    }

    const numValue = parseFloat(rawValue);
    if (isNaN(numValue)) {
      setCustomRateError(
        'Enter only numbers with "%" or a decimal value (e.g., 12% or 0.12).'
      );
      return;
    }

    if (rawValue.endsWith('%')) {
      if (numValue < 0 || numValue > 100) {
        setCustomRateError(
          'Enter a percentage no greater than 100% (e.g., 75%).'
        );
        return;
      }
    } else {
      const isWholeNumber = Number.isInteger(numValue);

      if (isWholeNumber) {
        if (numValue < 0 || numValue > 1) {
          setCustomRateError(
            'Enter a valid percentage (e.g., 12%) or a decimal value no greater than 1 (e.g., 0.75).'
          );
          return;
        }
      } else {
        if (numValue < 0 || numValue > 1) {
          setCustomRateError(
            'Decimal values must be 1 or less (e.g., 0.75). Use a percentage instead if needed (e.g., 75%).'
          );
          return;
        }
      }
    }

    setCustomRateError('');
  };

  const handleApply = () => {
    if (!customRateError && royaltyRate) {
      if (royaltyRate.endsWith('%')) {
        const percentageStr = royaltyRate.slice(0, -1).trim();
        const decimalValue = (parseFloat(percentageStr) / 100).toString();
        onApply(decimalValue);
      } else {
        onApply(royaltyRate);
      }
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      passiveModal={false}
      hasScrollingContent
      size="sm"
      modalHeading="Custom Royalty Rate"
      primaryButtonText="Apply"
      secondaryButtonText="Cancel"
      onRequestClose={onClose}
      onRequestSubmit={handleApply}
      onSecondarySubmit={onClose}
      primaryButtonDisabled={!isDirty()}
      closeButtonLabel="Close"
      aria-label="Configure royalty rate"
    >
      <ModalBody>
        <p className={styles.modalDescription}>
          This royalty rate will be applied to all unleased assets, as
          highlighted in the CRM Assets tables. These assets currently lack a
          lease royalty rate, causing the Tract NRI to be 0, which results in a
          Unit NRI of 0. Use the option below to select a predefined royalty
          rate or enter a custom value to ensure these unleased assets reflect
          an NRI value.
        </p>

        <div className={styles.rateSelectionContainer}>
          <p className={styles.rateLabel}>Royalty Rate</p>
          <RadioButtonGroup
            name="royalty-rate"
            valueSelected={isCustomRate ? 'custom' : royaltyRate}
            onChange={(value: string | number | undefined) => {
              if (value === 'custom') {
                setRoyaltyRate('');
              } else {
                setRoyaltyRate(value as string);
              }
            }}
            orientation="horizontal"
          >
            {Object.values(ROYALTY_RATE_OPTIONS).map((rate) => (
              <RadioButton
                key={rate}
                labelText={rate}
                value={rate}
                id={`rate-${rate}`}
              />
            ))}
            <RadioButton labelText="Custom" value="custom" id="custom-rate" />
          </RadioButtonGroup>
          {isCustomRate && (
            <div className={styles.customRateInput}>
              <TextInput
                id="custom-rate-input"
                labelText=""
                placeholder="Enter Custom Royalty Rate"
                value={royaltyRate}
                onChange={handleCustomRateChange}
                invalid={!!customRateError}
                invalidText={customRateError}
              />
              <p className={styles.inputHelperText}>
                {!customRateError &&
                  'Enter a valid percentage (e.g., 12%) or a decimal value no greater than 1 (e.g., 0.75).'}
              </p>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};
